import React, { useState, useEffect } from 'react';
import { Drawer, SIZE as DRAWER_SIZE } from 'baseui/drawer';
import { Button, KIND, SIZE } from 'baseui/button';
import { Input } from 'baseui/input';
import { FormControl } from 'baseui/form-control';
import { styled } from 'styletron-react';
import { AccessSetting, CompanionSettings, CustomCategory, useUpdateAccessSetting, useDeleteAccessSetting } from '../hooks/useAccessSettings';
import { Trash2, Plus } from 'lucide-react';
import { HeadingMedium } from 'baseui/typography';
import { BilingualField } from './BilingualField';
import { slugify } from '../utils/slug';
import { useFdCan } from '../hooks/useFdCan';

const emptyCustomCategory: CustomCategory = {
  key: '',
  min: 0,
  max: 0,
  title_en: '',
  title_es: '',
  subtitle_en: '',
  subtitle_es: '',
};

const DrawerComponent = Drawer as any;
const FormControlComponent = FormControl as any;
const InputComponent = Input as any;

const DrawerContent = styled('div', {
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  overflow: 'auto',
  flex: 1,
});

const DrawerFooter = styled('div', {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  padding: '16px 24px',
  borderTop: '1px solid #E5E7EB',
});

const SectionHeader = styled('div', {
  fontSize: '13px',
  fontWeight: 600,
  color: '#6B7280',
  marginBottom: '4px',
  paddingTop: '8px',
  borderTop: '1px solid #E9ECEF',
});

const FormRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
});

interface AccessSettingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  setting: AccessSetting | null;
}

export const AccessSettingDrawer: React.FC<AccessSettingDrawerProps> = ({
  isOpen,
  onClose,
  setting,
}) => {
  const updateMutation = useUpdateAccessSetting();
  const deleteMutation = useDeleteAccessSetting();

  const [editedName, setEditedName] = useState('');
  const [formData, setFormData] = useState<CompanionSettings | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>('en');

  useEffect(() => {
    if (setting) {
      setEditedName(setting.name || '');
      setFormData(setting.settings?.event?.[0] || null);
    }
  }, [setting]);

  // The tenant Front Desk views access settings but does not change them (specs/front-desk-tenant-permisos.md).
  const canEdit = useFdCan('events', 'edit');

  if (!setting || !formData) return null;

  const updateAdults = (field: string, value: any) => {
    setFormData(prev => prev ? { ...prev, adults: { ...prev.adults, [field]: value } } : prev);
  };

  const updateChild = (field: string, value: any) => {
    setFormData(prev => prev ? {
      ...prev,
      child: {
        min_num: 0, max_num: 0, name_en: '', name_es: '', subtitle_en: '', subtitle_es: '',
        ...prev.child,
        [field]: value,
      },
    } : prev);
  };

  const removeChildSection = () => {
    setFormData(prev => prev ? { ...prev, child: undefined } : prev);
  };

  const addChildSection = () => {
    setFormData(prev => prev ? {
      ...prev,
      child: { min_num: 0, max_num: 6, name_en: 'Children', name_es: 'Niños', subtitle_en: '', subtitle_es: '' },
    } : prev);
  };

  const addCustomCategory = () => {
    setFormData(prev => prev ? { ...prev, categories: [...(prev.categories || []), { ...emptyCustomCategory }] } : prev);
  };

  const updateCustomCategory = (index: number, field: keyof CustomCategory, value: any) => {
    setFormData(prev => {
      if (!prev) return prev;
      const categories = [...(prev.categories || [])];
      const updated = { ...categories[index], [field]: value };
      // Auto-derive `key` from the EN label so users don't have to manage it.
      if (field === 'title_en') {
        updated.key = slugify(typeof value === 'string' ? value : '');
      }
      categories[index] = updated;
      return { ...prev, categories };
    });
  };

  const removeCustomCategory = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const categories = [...(prev.categories || [])];
      categories.splice(index, 1);
      return { ...prev, categories };
    });
  };

  // Only one custom category may be flagged as main_guest at a time.
  // Toggling a category off clears the flag for everyone.
  const setMainGuestCategory = (index: number, makeMain: boolean) => {
    setFormData(prev => {
      if (!prev) return prev;
      const categories = (prev.categories || []).map((c, i) => ({
        ...c,
        is_main_guest: makeMain && i === index,
      }));
      return { ...prev, categories };
    });
  };

  const handleSave = async () => {
    if (!formData) return;
    try {
      await updateMutation.mutateAsync({
        id: setting.id,
        business_id: setting.business_id,
        name: editedName,
        settings: { event: [formData] },
      });
      onClose();
    } catch (error) {
      console.error('Error updating:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this access setting? This cannot be undone.')) return;
    try {
      await deleteMutation.mutateAsync(setting.id);
      onClose();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  return (
    <DrawerComponent isOpen={isOpen} onClose={onClose} size={DRAWER_SIZE.auto}
      overrides={{ Root: { style: { zIndex: 10000 } }, DrawerContainer: { style: { width: '520px', maxWidth: '90vw' } } }}>
      {/* Keyed by setting.id: if a different row is clicked while the drawer is
          already open, this remounts the whole form (including every BilingualField)
          instead of reusing the mounted instances, so the "dirty" flags computed
          from the previous record's values can't leak into the newly selected one. */}
      <DrawerContent key={setting.id}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <HeadingMedium margin={0}>Edit Access Setting</HeadingMedium>
          {canEdit && (
            <Button size={SIZE.compact} kind={KIND.tertiary} onClick={handleDelete}
              overrides={{ BaseButton: { style: { color: '#DC2626' } } }}>
              <Trash2 size={16} />
            </Button>
          )}
        </div>

        <FormControlComponent label="Name *">
          <InputComponent size="compact" value={editedName} onChange={(e: any) => setEditedName(e.target.value)} />
        </FormControlComponent>

        {/* Language tab — controls which language the bilingual fields below show.
            Typing in one language auto-translates the other (debounced 700ms). */}
        <div style={{ display: 'flex', gap: 4, padding: 4, background: '#F3F4F6', borderRadius: 8 }}>
          {(['en', 'es'] as const).map(lng => (
            <button
              key={lng}
              type="button"
              onClick={() => setLanguage(lng)}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 6,
                background: language === lng ? '#fff' : 'transparent',
                boxShadow: language === lng ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                color: language === lng ? '#111827' : '#6B7280',
              }}
            >
              {lng === 'en' ? 'English' : 'Español'}
            </button>
          ))}
        </div>

        <BilingualField
          label="Title"
          language={language}
          valueEn={formData.title_en}
          valueEs={formData.title_es}
          onChange={(lang, val) => setFormData(prev => prev ? { ...prev, [`title_${lang}`]: val } : prev)}
        />

        {/* Adults */}
        <SectionHeader>Adults Configuration</SectionHeader>

        <FormRow>
          <FormControlComponent label="Min">
            <InputComponent type="number" size="compact" value={formData.adults.min} onChange={(e: any) => updateAdults('min', parseInt(e.target.value) || 0)} />
          </FormControlComponent>
          <FormControlComponent label="Max">
            <InputComponent type="number" size="compact" value={formData.adults.max} onChange={(e: any) => updateAdults('max', parseInt(e.target.value) || 0)} />
          </FormControlComponent>
        </FormRow>

        <BilingualField
          label="Label"
          language={language}
          valueEn={formData.adults.title_en}
          valueEs={formData.adults.title_es}
          onChange={(lang, val) => updateAdults(`title_${lang}`, val)}
        />

        <BilingualField
          label="Subtitle"
          language={language}
          valueEn={formData.adults.subtitle_en}
          valueEs={formData.adults.subtitle_es}
          onChange={(lang, val) => updateAdults(`subtitle_${lang}`, val)}
        />

        {/* Children — optional section, can be deleted entirely */}
        {formData.child ? (
          <>
            <SectionHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Children Configuration</span>
                <Button size={SIZE.mini} kind={KIND.tertiary} onClick={removeChildSection}
                  overrides={{ BaseButton: { style: { color: '#DC2626' } } }}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </SectionHeader>

            <FormRow>
              <FormControlComponent label="Min">
                <InputComponent type="number" size="compact" value={formData.child.min_num} onChange={(e: any) => updateChild('min_num', parseInt(e.target.value) || 0)} />
              </FormControlComponent>
              <FormControlComponent label="Max">
                <InputComponent type="number" size="compact" value={formData.child.max_num} onChange={(e: any) => updateChild('max_num', parseInt(e.target.value) || 0)} />
              </FormControlComponent>
            </FormRow>

            <BilingualField
              label="Label"
              language={language}
              valueEn={formData.child.name_en}
              valueEs={formData.child.name_es}
              onChange={(lang, val) => updateChild(`name_${lang}`, val)}
            />

            <BilingualField
              label="Subtitle"
              language={language}
              valueEn={formData.child.subtitle_en}
              valueEs={formData.child.subtitle_es}
              onChange={(lang, val) => updateChild(`subtitle_${lang}`, val)}
            />
          </>
        ) : (
          <Button size={SIZE.compact} kind={KIND.secondary} onClick={addChildSection}>
            <Plus size={14} style={{ marginRight: 6 }} /> Add children section
          </Button>
        )}

        {/* Custom Categories — beyond adults/child (e.g. athlete, vip, spectator) */}
        <SectionHeader>Custom Categories</SectionHeader>

        {(formData.categories || []).map((cat, idx) => (
          <div key={idx} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 13, color: '#111827' }}>Category #{idx + 1}</strong>
              <Button size={SIZE.mini} kind={KIND.tertiary} onClick={() => removeCustomCategory(idx)}
                overrides={{ BaseButton: { style: { color: '#DC2626' } } }}>
                <Trash2 size={14} />
              </Button>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={!!cat.is_main_guest}
                onChange={(e) => setMainGuestCategory(idx, e.target.checked)}
              />
              <span>Use this category as <strong>main guest</strong></span>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>(otherwise the first adult is the main guest)</span>
            </label>

            <FormRow>
              <FormControlComponent label="Min">
                <InputComponent type="number" size="compact" value={cat.min} onChange={(e: any) => updateCustomCategory(idx, 'min', parseInt(e.target.value) || 0)} />
              </FormControlComponent>
              <FormControlComponent label="Max">
                <InputComponent type="number" size="compact" value={cat.max} onChange={(e: any) => updateCustomCategory(idx, 'max', parseInt(e.target.value) || 0)} />
              </FormControlComponent>
            </FormRow>

            <BilingualField
              label="Label"
              language={language}
              valueEn={cat.title_en}
              valueEs={cat.title_es}
              onChange={(lang, val) => updateCustomCategory(idx, `title_${lang}` as keyof CustomCategory, val)}
            />

            <BilingualField
              label="Subtitle"
              language={language}
              valueEn={cat.subtitle_en || ''}
              valueEs={cat.subtitle_es || ''}
              onChange={(lang, val) => updateCustomCategory(idx, `subtitle_${lang}` as keyof CustomCategory, val)}
            />
          </div>
        ))}

        <Button size={SIZE.compact} kind={KIND.secondary} onClick={addCustomCategory}>
          <Plus size={14} style={{ marginRight: 6 }} /> Add category
        </Button>
      </DrawerContent>

      <DrawerFooter>
        <Button kind={KIND.secondary} size={SIZE.compact} onClick={onClose}>Cancel</Button>
        {canEdit && (
          <Button size={SIZE.compact} onClick={handleSave} isLoading={updateMutation.isPending} disabled={!editedName}
            overrides={{ BaseButton: { style: { backgroundColor: 'var(--tenant-primary, #111827)' } } }}>
            Save Changes
          </Button>
        )}
      </DrawerFooter>
    </DrawerComponent>
  );
};
